import apollo from "@/apollo.js"
import gql from "graphql-tag"
import { jobsUiDebugLog } from "@/utils/jobsUiDebug"

export const getJobsPage = async (data) => {
  jobsUiDebugLog("getJobsPage:start", {
    page: data.page,
    pageSize: data.pageSize,
    myJobsOnly: data.myJobsOnly,
    jobStatus: data.jobStatus,
    jobType: data.jobType,
  })
  const response = await apollo.query({
    query: gql(`
      query jobs($page: Int!, $pageSize: Int!, $myJobsOnly: Boolean, $jobStatus: String, $jobType: String) {
        jobs(page: $page, pageSize: $pageSize, myJobsOnly: $myJobsOnly, jobStatus: $jobStatus, jobType: $jobType) {
          jobs { id jobType jobStatus currentStep totalSteps causedBy { id } createdAt updatedAt failed succeeded completedCaseIds }
          pageTotal
        } 
      }
      `),
    variables: { page: data.page, pageSize: data.pageSize, myJobsOnly: data.myJobsOnly, jobStatus: data.jobStatus, jobType: data.jobType },
  })
  const jobsPayload = response.data && response.data.jobs ? response.data.jobs : null
  if (jobsPayload && jobsPayload.jobs && jobsPayload.jobs.length > 0) {
    const first = jobsPayload.jobs[0]
    jobsUiDebugLog("getJobsPage:ok", {
      pageTotal: jobsPayload.pageTotal,
      rowCount: jobsPayload.jobs.length,
      firstId: first.id,
      firstStatus: first.jobStatus,
      firstSucceeded: first.succeeded,
      firstFailed: first.failed,
      firstStep: first.currentStep,
      firstTotalSteps: first.totalSteps,
    })
  } else {
    jobsUiDebugLog("getJobsPage:ok", {
      pageTotal: jobsPayload && jobsPayload.pageTotal,
      rowCount: jobsPayload && jobsPayload.jobs ? jobsPayload.jobs.length : 0,
    })
  }
  return response.data.jobs
}

export const getJobTypes = async (myJobsOnly) => {
  const response = await apollo.query({
    query: gql(`
      query jobTypes($myJobsOnly: Boolean) {
        jobTypes(myJobsOnly: $myJobsOnly)
      }
      `),
    variables: { myJobsOnly },
    fetchPolicy: "network-only",
  })
  return response.data.jobTypes
}

export const getJob = async (id) => {
  const response = await apollo.query({
    query: gql(`
      query job($id: Int!) {
      job(id: $id) {
        job { id jobType jobStatus currentStep totalSteps causedBy { id } createdAt updatedAt failed succeeded completedCaseIds}
      }
    }
      `),
    variables: { id: parseInt(id) },
  })
  return response.data.job.job
}

export const getJobFailureCases = async (id) => {
  const response = await apollo.query({
    query: gql(`
      query jobFailureCases($id: Int!) {
        job(id: $id) {
          job { failureCases { caseTypeId caseIds } }
        }
      }
    `),
    variables: { id: parseInt(id, 10) },
    fetchPolicy: "network-only",
  })
  const job = response.data && response.data.job && response.data.job.job
  return job && job.failureCases ? job.failureCases : { caseTypeId: null, caseIds: [] }
}

/** Poll while a job runs: merges live Redis counters server-side; use instead of getJob in timers. */
export const getJobStats = async (id) => {
  const jobId = parseInt(id, 10)
  jobsUiDebugLog("getJobStats:start", { jobId: jobId })
  const response = await apollo.query({
    query: gql(`
      query jobStats($id: Int!) {
        jobStats(id: $id) {
          error
          message
          lowLevelErrorMessage
          snapshot {
            id
            jobType
            jobStatus
            currentStep
            totalSteps
            succeeded
            failed
            createdAt
            updatedAt
            countersFromRedis
          }
        }
      }
    `),
    variables: { id: jobId },
    fetchPolicy: "network-only",
  })
  const payload = response.data && response.data.jobStats ? response.data.jobStats : null
  if (!payload || payload.error || !payload.snapshot) {
    jobsUiDebugLog("getJobStats:null", {
      jobId: jobId,
      graphQlError: !!(payload && payload.error),
      message: payload && payload.message,
      lowLevelErrorMessage: payload && payload.lowLevelErrorMessage,
    })
    return null
  }
  const snap = payload.snapshot
  jobsUiDebugLog("getJobStats:ok", {
    jobId: jobId,
    jobStatus: snap.jobStatus,
    succeeded: snap.succeeded,
    failed: snap.failed,
    currentStep: snap.currentStep,
    totalSteps: snap.totalSteps,
    countersFromRedis: snap.countersFromRedis,
  })
  return snap
}

export const killJob = async (id) => {
  const response = await apollo.mutate({
    mutation: gql(`
      mutation killJob($jobId: Int!) {
        killJob(jobId: $jobId) {
          error
          message
        }
      }
    `),
    variables: { jobId: parseInt(id) },
  })
  return response.data.killJob
}

let jobStatsSubscriptionObserver = null

export const subscribeToJobStatsUpdates = (onUpdate) => {
  jobsUiDebugLog("jobStatsSubscribe:register", {})
  try {
    if (jobStatsSubscriptionObserver) {
      jobStatsSubscriptionObserver.unsubscribe()
      jobStatsSubscriptionObserver = null
    }
    if (!apollo || typeof apollo.subscribe !== "function") {
      jobsUiDebugLog("jobStatsSubscribe:no_apollo", { hasApollo: !!apollo })
      return null
    }
    const observable = apollo.subscribe({
      query: gql(`
        subscription jobStatsUpdated {
          jobStatsUpdated {
            update {
              jobId
              accountCode
              jobType
              jobStatus
              causedById
              currentStep
              totalSteps
              succeeded
              failed
              createdAt
              updatedAt
            }
          }
        }
      `),
    })
    if (!observable) {
      jobsUiDebugLog("jobStatsSubscribe:no_observable", {})
      return null
    }
    const subscription = observable.subscribe({
      next(response) {
        if (response && response.data && response.data.jobStatsUpdated && response.data.jobStatsUpdated.update) {
          const update = response.data.jobStatsUpdated.update
          jobsUiDebugLog("jobStatsSubscribe:next", {
            jobId: update.jobId,
            jobStatus: update.jobStatus,
            succeeded: update.succeeded,
            failed: update.failed,
            currentStep: update.currentStep,
            totalSteps: update.totalSteps,
            accountCode: update.accountCode,
          })
          if (onUpdate) {
            onUpdate(update)
          }
        } else {
          jobsUiDebugLog("jobStatsSubscribe:next_empty", {
            hasResponse: !!response,
            hasData: !!(response && response.data),
          })
        }
      },
      error(err) {
        jobsUiDebugLog("jobStatsSubscribe:error", {
          message: err && err.message ? err.message : String(err),
        })
        window.setTimeout(() => {
          subscribeToJobStatsUpdates(onUpdate)
        }, 2000)
      },
      complete() {
        jobsUiDebugLog("jobStatsSubscribe:complete", {})
        window.setTimeout(() => {
          subscribeToJobStatsUpdates(onUpdate)
        }, 2000)
      },
    })
    jobStatsSubscriptionObserver = subscription
    jobsUiDebugLog("jobStatsSubscribe:subscribed", {})
    return subscription
  } catch (error) {
    jobsUiDebugLog("jobStatsSubscribe:exception", {
      message: error && error.message ? error.message : String(error),
    })
    window.setTimeout(() => {
      subscribeToJobStatsUpdates(onUpdate)
    }, 2000)
    return null
  }
}

export const unsubscribeFromJobStatsUpdates = () => {
  jobsUiDebugLog("jobStatsSubscribe:unsubscribe", {})
  if (jobStatsSubscriptionObserver) {
    jobStatsSubscriptionObserver.unsubscribe()
    jobStatsSubscriptionObserver = null
  }
}

export const getJobLogs = async (jobId, page, pageSize, sortBy, descending) => {
  const sortDirection = descending ? "desc" : "asc"
  const response = await apollo.query({
    query: gql(`
      query job($page: Int!, $pageSize: Int!, $jobId: Int!, $sortBy: String!, $sortDirection: String! ) {
        job(id: $jobId) {
          job {logs(page: $page, pageSize: $pageSize, sortBy: $sortBy, sortDirection: $sortDirection) {id message caseTitle createdAt isError logs { timestamp level message }}}
        } 
      }
      `),
    variables: { jobId, page, pageSize, sortBy, sortDirection },
  })
  console.log(response.data)
  return response.data.job
}
